import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, PrimaryButton, GhostButton, TextField, Label, StatusBadge } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Eye, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

const selCls = "w-full mt-1.5 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm";

export default function ProductionRequests() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [list, setList] = useState([]);
  const [product, setProduct] = useState("");
  const [qty, setQty] = useState("");
  const [lines, setLines] = useState([{ item_id: "", quantity: "" }]);
  const [reqDate, setReqDate] = useState("");
  const [notes, setNotes] = useState("");
  const [feas, setFeas] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const canCreate = hasPerm("warehouse:create") || hasPerm("production:create");
  const canApprove = hasPerm("production:approve");
  const finished = items.filter((i) => i.item_type === "finished_good");
  const raws = items.filter((i) => i.item_type === "raw_material");

  const loadRefs = async () => { try { const { data } = await api.get("/refs/items"); setItems(data); } catch { } };
  const loadList = async () => { try { const { data } = await api.get("/production-requests"); setList(data); } catch { } };
  useEffect(() => { loadRefs(); loadList(); }, []);

  const body = () => ({
    product_item_id: product, quantity: Number(qty || 0), required_date: reqDate || null, notes,
    materials: lines.filter((l) => l.item_id && l.quantity).map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity) })),
  });
  const check = async () => { try { const { data } = await api.post("/production-requests/check", body()); setFeas(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/production-requests", body());
      toast.success("Production request submitted");
      setProduct(""); setQty(""); setLines([{ item_id: "", quantity: "" }]); setReqDate(""); setNotes(""); setFeas(null);
      await loadList();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit"); }
    finally { setSaving(false); }
  };
  const act = async (r, action) => {
    try {
      await api.post(`/production-requests/${r.id}/${action}`);
      toast.success(`Request ${action} ✓`);
      setDetail(null);
      await loadList();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openDetail = async (r) => {
    setDetailLoading(true); setDetail({ id: r.id, loading: true });
    try {
      const { data } = await api.get(`/production-requests/${r.id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setDetail(null); }
    finally { setDetailLoading(false); }
  };
  const setLine = (i, k, v) => setLines((p) => p.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  const flines = detail?.live_feasibility?.lines || [];
  const isFeasible = detail?.live_feasibility?.is_feasible;

  return (
    <div className="space-y-6" data-testid="production-requests-page">
      <PageHeader title="Production Requests" subtitle="Request finished products to be produced; the system checks raw-material availability." />
      {canCreate && (
        <Panel className="p-4" data-testid="pr-form">
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Product to produce</Label><select required value={product} onChange={(e) => setProduct(e.target.value)} data-testid="pr-product" className={selCls}><option value="">— select —</option>{finished.map((i) => <option key={i.id} value={i.id}>{i.sku} — {i.name}</option>)}</select></div>
              <div><Label>Quantity</Label><TextField type="number" required value={qty} onChange={(e) => setQty(e.target.value)} data-testid="pr-qty" /></div>
              <div><Label>Required date</Label><TextField type="date" value={reqDate} onChange={(e) => setReqDate(e.target.value)} data-testid="pr-date" /></div>
            </div>
            <div>
              <Label>Required raw materials</Label>
              <div className="space-y-2 mt-1.5">{lines.map((l, i) => (
                <div key={i} className="flex gap-2">
                  <select value={l.item_id} onChange={(e) => setLine(i, "item_id", e.target.value)} data-testid={`pr-mat-${i}`} className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"><option value="">— material —</option>{raws.map((r) => <option key={r.id} value={r.id}>{r.sku} — {r.name}</option>)}</select>
                  <TextField type="number" placeholder="qty" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`pr-mat-qty-${i}`} className="w-28" />
                  {lines.length > 1 && <button type="button" onClick={() => setLines((p) => p.filter((_, x) => x !== i))} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>}
                </div>))}</div>
              <GhostButton type="button" className="mt-2" onClick={() => setLines((p) => [...p, { item_id: "", quantity: "" }])} data-testid="pr-add-line"><Plus className="h-3.5 w-3.5 inline" /> Add material</GhostButton>
            </div>
            <div><Label>Notes</Label><TextField value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            {feas && (
              <div className={`rounded p-3 text-sm border ${feas.is_feasible ? "border-emerald-600/40 bg-emerald-600/10 text-emerald-300" : "border-rose-600/40 bg-rose-600/10 text-rose-300"}`} data-testid="pr-feas-result">
                <b>{feas.is_feasible ? "Production is possible ✓" : "Not possible — shortfalls:"}</b>
                {!feas.is_feasible && <ul className="mt-1 list-disc ml-5">{feas.lines.filter((x) => x.shortfall > 0).map((x, i) => <li key={i}>{x.name || x.sku}: need {x.required}, have {x.available} (short {x.shortfall})</li>)}</ul>}
              </div>
            )}
            <div className="flex gap-2"><GhostButton type="button" onClick={check} data-testid="pr-check">Check feasibility</GhostButton><PrimaryButton type="submit" disabled={saving} data-testid="pr-submit">Submit request</PrimaryButton></div>
          </form>
        </Panel>
      )}
      <Panel className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="pr-list">
          <thead><tr className="text-left font-mono text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
            <th className="px-4 py-3">Request #</th><th className="px-4 py-3">Product</th><th className="px-4 py-3 text-right">Qty</th>
            <th className="px-4 py-3">Feasible</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">By</th><th className="px-4 py-3 text-right">Actions</th>
          </tr></thead>
          <tbody>{list.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No production requests yet.</td></tr>
            : list.map((r) => (<tr key={r.id} className="border-b border-slate-800/60" data-testid={`pr-row-${r.request_number}`}>
              <td className="px-4 py-3 font-mono text-orange-400">{r.request_number}</td>
              <td className="px-4 py-3">{r.product_name || "—"}</td>
              <td className="px-4 py-3 text-right font-mono">{r.quantity}</td>
              <td className="px-4 py-3"><span className={`text-xs font-mono uppercase ${r.is_feasible ? "text-emerald-400" : "text-rose-400"}`}>{r.is_feasible ? "possible" : "shortfall"}</span></td>
              <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3 text-slate-400 text-xs">{r.created_by_name || "—"}</td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <GhostButton className="mr-1.5" onClick={() => openDetail(r)} data-testid={`pr-view-${r.request_number}`}><Eye className="h-3.5 w-3.5 inline" /> view</GhostButton>
              </td>
            </tr>))}</tbody>
        </table>
      </Panel>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pr-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-wide">Production Request {detail?.request_number || ""}</DialogTitle>
            <DialogDescription className="text-slate-400">Review required materials against current stock before approving.</DialogDescription>
          </DialogHeader>
          {detailLoading || detail?.loading ? (
            <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-orange-500 inline" /></div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="font-mono text-[11px] uppercase text-slate-500">Product</div><div className="text-slate-200">{detail.product_name || "—"}</div></div>
                <div><div className="font-mono text-[11px] uppercase text-slate-500">Quantity</div><div className="text-slate-200 font-mono">{detail.quantity}</div></div>
                <div><div className="font-mono text-[11px] uppercase text-slate-500">Status</div><StatusBadge status={detail.status} /></div>
                <div><div className="font-mono text-[11px] uppercase text-slate-500">Requested by</div><div className="text-slate-200">{detail.created_by_name || "—"}</div></div>
              </div>

              <div className={`rounded-md p-3 border flex items-center gap-2 text-sm ${isFeasible ? "border-emerald-600/40 bg-emerald-600/10 text-emerald-300" : "border-amber-600/40 bg-amber-600/10 text-amber-300"}`} data-testid="pr-detail-feasibility">
                {isFeasible ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                <span><b>{isFeasible ? "All materials available." : "Warning: insufficient stock for one or more materials."}</b> Live check against current inventory.</span>
              </div>

              <div>
                <div className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-2">Materials needed vs available</div>
                <table className="w-full text-sm border border-slate-800 rounded" data-testid="pr-detail-materials">
                  <thead><tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800 bg-slate-900/50">
                    <th className="px-3 py-2">Material</th><th className="px-3 py-2 text-right">Needed</th><th className="px-3 py-2 text-right">Available</th><th className="px-3 py-2 text-right">Shortfall</th>
                  </tr></thead>
                  <tbody>{flines.map((x, i) => (
                    <tr key={i} className="border-b border-slate-800/60" data-testid={`pr-detail-mat-row-${i}`}>
                      <td className="px-3 py-2 text-slate-200">{x.name || x.sku || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{x.required}</td>
                      <td className="px-3 py-2 text-right font-mono">{x.available}</td>
                      <td className={`px-3 py-2 text-right font-mono ${x.shortfall > 0 ? "text-rose-400 font-bold" : "text-emerald-400"}`}>{x.shortfall > 0 ? `-${x.shortfall}` : "0"}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <GhostButton type="button" onClick={() => setDetail(null)}>Close</GhostButton>
            {detail && !detail.loading && detail.status === "submitted" && canApprove && (
              <>
                <button onClick={() => act(detail, "reject")} className="text-slate-400 hover:text-rose-400 text-sm px-3" data-testid="pr-detail-reject">Reject</button>
                <PrimaryButton onClick={() => act(detail, "approve")} data-testid="pr-detail-approve">
                  {isFeasible ? "Approve" : "Approve anyway"}
                </PrimaryButton>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
